import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faEmptySet } from '@fortawesome/pro-regular-svg-icons';
import { twMerge } from 'tailwind-merge';

type EmptyStateProps = {
  title: string;
  description: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
};

const sizeStyles = {
  sm: {
    container: 'p-2',
    icon: 'text-2xl',
    title: 'text-xs',
    description: 'text-xs',
    gap: 'gap-2',
    padding: 'py-2 px-2',
  },
  md: {
    container: 'p-4',
    icon: 'text-4xl',
    title: 'text-base',
    description: 'text-sm',
    gap: 'gap-4',
    padding: 'py-8 px-4',
  },
  lg: {
    container: 'p-6',
    icon: 'text-6xl',
    title: 'text-lg',
    description: 'text-base',
    gap: 'gap-6',
    padding: 'py-12 px-6',
  },
} as const;

const EmptyState = ({
  title,
  description,
  icon,
  className,
  size = 'md',
  action,
}: EmptyStateProps) => {
  const styles = sizeStyles[size];

  return (
    <div
      className={twMerge(
        'border border-neutral-200 bg-neutral-050 rounded-lg flex flex-col shadow-sm',
        styles.container,
        className
      )}
      data-testid="empty-state-container"
      aria-label={title}
    >
      <div
        className={twMerge(
          'flex flex-col items-center justify-center text-center',
          styles.padding,
          styles.gap
        )}
      >
        <div className={twMerge('flex flex-col items-center', styles.gap)}>
          {icon || (
            <Icon
              icon={faEmptySet}
              className={twMerge(styles.icon, 'text-neutral-400/80')}
              data-testid="empty-state-icon"
            />
          )}
          <div className="flex flex-col gap-1 max-w-lg">
            <h4
              className={twMerge(
                styles.title,
                'font-semibold text-neutral-600'
              )}
            >
              {title}
            </h4>
            <p
              className={twMerge(
                styles.description,
                'text-neutral-600 leading-relaxed'
              )}
            >
              {description}
            </p>
          </div>
        </div>
        {action && action}
      </div>
    </div>
  );
};

export default EmptyState;
